import React, { useLayoutEffect, useRef } from 'react';

import {
  FlowContextProvider,
  FlowValues,
  StepWrapper,
  useAutoNavigate,
  useMutation,
  useQuery,
  useReturnToPath,
  useSessionStorage,
  when,
} from '@betterment/js-runtime';
import { useFeatureFlag } from '@betterment/shared-components';
import { pick as _pick } from 'es-toolkit/compat';
import { Route, Routes, useLocation, useMatch, useParams, useSearchParams } from 'react-router-dom';

import AccountTypeSelectionStep from './AccountTypeSelectionStep';
import BeginSetup from './BeginSetup';
import BeginVerification from './BeginVerification';
import ConfirmAddresses from './ConfirmAddresses';
import Disclosure from './Disclosure';
import FundAccount from './FundAccount';
import LimitError from './LimitError';
import Occupation, { CHECKING_FLOW_UPDATES_FLAG } from './Occupation';
import SourceOfFundsStep from './SourceOfFundsStep';
import StateAgeLimitEligibilityCheck from './StateAgeLimitEligibilityCheck';
import Success from './Success';

import { useCustomerResponse } from '../Checking/components/CustomerResponseProvider';
import { useBankDocuments } from '../Checking/contexts/BankDocumentsProvider';
import { useUser } from '../Checking/contexts/UserProvider';
import {
  Address,
  AddressParams,
  AddressValidationParams,
  Customer,
  CustomerCreateParams,
  CustomerCreateResponse,
} from '../Checking/data';
import { hasMaxAccounts, isVerified } from '../Checking/models/customer';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { CheckingSignupData, CheckingSignupDataParams } from '../Shared/data';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

export const customerValues: CustomerFlowValues = {
  bankAgreements: true,
  retailAgreements: true,
  residentialAddress: {} as Address,
  mailingAddress: {} as Address,
  nextStep: undefined,
  customer: undefined,
  invitationId: undefined,
  accountId: null,
  accountType: 'individual',
  role: undefined,
  employmentStatus: undefined,
  employerName: undefined,
  sourceOfFunds: undefined,
  stepTitle: 'Checking signup',
};

export interface CustomerFlowValues extends AddressParams, CustomerCreateParams, Partial<CustomerCreateResponse> {
  customer?: Customer | null;
  accountId: number | null;
  accountType?: 'individual' | 'joint';
  sourceOfFunds?: string;
  role?: string;
  employmentStatus?: string;
  employerName?: string;
  stepTitle: string;
}

export interface AddressFormValues extends AddressParams {
  fullName: string;
}
export type CustomerSignupValues = FlowValues<CustomerFlowValues>;

type FlowVariant = 'prospect' | 'tenured';

interface CustomerSignupFlowControllerValues {
  variant?: FlowVariant;
}

export const getStepTitle = (accountType: 'joint' | 'individual' | undefined) =>
  accountType === 'joint' ? 'Joint Checking signup' : 'Checking signup';

const getStepAfterConfirmAddresses = (
  requiresOccupation: boolean | undefined,
  shouldShowSourceOfFunds: boolean
): string => {
  if (requiresOccupation) {
    return 'occupation';
  }
  if (shouldShowSourceOfFunds) {
    return 'source_of_funds';
  }
  return 'submit';
};

interface InitialStepOrderParams {
  isTenuredSignupFlow: boolean;
  isTenuredCheckingCustomer: boolean;
  signupInvitationId?: string;
  isJointSignupFlow: boolean;
}

const getInitialStepOrder = ({
  isTenuredSignupFlow,
  isTenuredCheckingCustomer,
  signupInvitationId,
  isJointSignupFlow,
}: InitialStepOrderParams): string[] => {
  const steps: string[] = [];
  if (isTenuredSignupFlow && !isTenuredCheckingCustomer) {
    steps.push('state_age_limit_eligibility_check');
  }
  if (isTenuredSignupFlow && !signupInvitationId && !isJointSignupFlow) {
    steps.push('account_type_selection');
  } else {
    steps.push('disclosure');
  }
  return steps;
};

type OwnershipType = 'joint' | 'individual';

interface ComputeSignupStateParams {
  invitationId?: string;
  sessionInvitationId?: string;
  accountType?: OwnershipType;
  sessionAccountType?: OwnershipType;
  variant: FlowVariant;
  customer?: Customer | null;
}

const computeSignupState = (params: ComputeSignupStateParams) => {
  const { invitationId, sessionInvitationId, accountType, sessionAccountType, variant, customer } = params;
  const signupInvitationId = invitationId ?? sessionInvitationId;
  const isJointSignupFlow = (accountType ?? sessionAccountType) === 'joint';
  const isProspectSignupFlow = variant === 'prospect';
  const isTenuredSignupFlow = variant === 'tenured';
  const isVerifiedCustomerWithAnAccount = Boolean(customer && isVerified(customer) && customer.accounts.length > 0);
  const isTenuredCheckingCustomer = isTenuredSignupFlow && isVerifiedCustomerWithAnAccount;
  const isSecondaryAcceptingInvitation = signupInvitationId !== undefined;
  const shouldShowSourceOfFunds = !isTenuredCheckingCustomer || isSecondaryAcceptingInvitation;

  return {
    signupInvitationId,
    isJointSignupFlow,
    isProspectSignupFlow,
    isTenuredSignupFlow,
    isTenuredCheckingCustomer,
    shouldShowSourceOfFunds,
  };
};

const CustomerSignupFlowController: React.FC<React.PropsWithChildren<CustomerSignupFlowControllerValues>> = ({
  variant = 'tenured',
}) => {
  const { user } = useUser();
  const { customer } = useCustomerResponse();
  const { data: documents, loading: isLoadingDocuments } = useBankDocuments();
  const { data: checkingSignupData, loading: isLoadingCheckingSignupData } = useQuery<CheckingSignupData>(
    urlBuilder.getCheckingSignupData.absolute()
  );
  const [mutateCheckingSignupData] = useMutation<undefined, CheckingSignupDataParams>(
    urlBuilder.getCheckingSignupData.absolute(),
    { method: 'put' }
  );
  const [validateAddress] = useMutation<object, AddressValidationParams>(urlBuilder.postValidateAddress());
  const matchA = useMatch('/checking/signups/:id/*');
  const matchB = useMatch('/checking/prospect_signup/identity/*');
  const match = matchA ?? matchB;
  const navigateRouter = useAutoNavigate();
  const returnToPath = useReturnToPath(urlBuilder.appHome.absolute());

  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get('invitation_id') ?? undefined;
  const accountType = (searchParams.get('account_type') as OwnershipType | null) ?? undefined;
  const { id } = useParams<{ id?: string }>();
  const { values: sessionValues, clearValues } = useSessionStorage<CustomerFlowValues>(
    `${id ?? 'prospect'}-checking-customer-signup`,
    (invitationId ?? accountType) ? { invitationId, accountType } : undefined
  );

  const { pathname } = useLocation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    modalOverlayRef.current?.scrollTo(0, 0);
  }, [pathname]);

  const signupState = computeSignupState({
    invitationId,
    sessionInvitationId: sessionValues?.invitationId,
    accountType,
    sessionAccountType: sessionValues?.accountType,
    variant,
    customer,
  });

  const residentialAddress = _pick(user?.residentialAddress, ['addressLine1', 'addressLine2', 'city', 'state', 'zip']);

  const handleClose = () => {
    clearValues();
    void navigateRouter(urlBuilder.appHome.absolute());
  };

  const persistCheckingSignupData = async (values: CheckingSignupDataParams) => {
    await mutateCheckingSignupData(values);
  };

  const { enabled: isNewFlowEnabled, loading: isCheckingFlowUpdatesLoading } = useFeatureFlag({
    splitName: CHECKING_FLOW_UPDATES_FLAG,
    context: 'checking_signup',
  });

  const stepAfterConfirmAddresses = isNewFlowEnabled
    ? 'occupation'
    : getStepAfterConfirmAddresses(user?.requiresOccupation, signupState.shouldShowSourceOfFunds);
  const initialStepOrder = getInitialStepOrder({
    isTenuredSignupFlow: signupState.isTenuredSignupFlow,
    isTenuredCheckingCustomer: signupState.isTenuredCheckingCustomer,
    signupInvitationId: signupState.signupInvitationId,
    isJointSignupFlow: signupState.isJointSignupFlow,
  });

  if (
    (!documents && isLoadingDocuments) ||
    (!checkingSignupData && isLoadingCheckingSignupData) ||
    isCheckingFlowUpdatesLoading ||
    !user ||
    !checkingSignupData ||
    !match
  ) {
    return <FullPageLoader />;
  }

  if (customer && hasMaxAccounts(customer)) {
    locationAssign(returnToPath);
    return null;
  }

  let previousStepForSourceOfFunds: string;
  if (isNewFlowEnabled || user.requiresOccupation) {
    previousStepForSourceOfFunds = 'occupation';
  } else {
    previousStepForSourceOfFunds = 'confirm_addresses';
  }

  return (
    <FlowContextProvider
      defaultStep={initialStepOrder[0]}
      returnToPath={returnToPath}
      initialValues={{
        ...customerValues,
        invitationId: signupState.signupInvitationId,
        accountType,
        customer,
        residentialAddress,
        mailingAddress: residentialAddress,
        stepTitle: getStepTitle(accountType),
      }}
      basePath={match.pathnameBase}
      routePath=""
    >
      <Routes>
        <Route
          path="/state_age_limit_eligibility_check"
          element={
            <StepWrapper name="state_age_limit_eligibility_check" next={initialStepOrder[1]}>
              <StateAgeLimitEligibilityCheck handleClose={handleClose} />
            </StepWrapper>
          }
        />
        <Route
          path="/account_type_selection"
          element={
            <StepWrapper<CustomerSignupValues>
              name="account_type_selection"
              next={[['disclosure', when(() => signupState.isTenuredSignupFlow)], 'fund_account']}
            >
              {stepProps => (
                <AccountTypeSelectionStep
                  {...stepProps}
                  isTenuredCheckingCustomer={signupState.isTenuredCheckingCustomer}
                  isProspectSignupFlow={signupState.isProspectSignupFlow}
                  customer={customer}
                  navigateRouter={navigateRouter}
                  handleClose={handleClose}
                />
              )}
            </StepWrapper>
          }
        />
        <Route
          path="/disclosure"
          element={
            <StepWrapper<CustomerSignupValues> name="disclosure">
              {stepProps => (
                <Disclosure {...stepProps} documents={documents ?? []} user={user} handleClose={handleClose} />
              )}
            </StepWrapper>
          }
        />
        <Route
          path="/confirm_addresses"
          element={
            <StepWrapper<CustomerSignupValues> name="confirm_addresses" next={stepAfterConfirmAddresses}>
              {stepProps => (
                <ConfirmAddresses
                  {...stepProps}
                  user={user}
                  validateAddress={validateAddress}
                  handleClose={handleClose}
                />
              )}
            </StepWrapper>
          }
        />
        <Route
          path="/occupation"
          element={
            <StepWrapper<CustomerSignupValues> name="occupation">
              {stepProps => (
                <Occupation
                  {...stepProps}
                  checkingSignupData={checkingSignupData}
                  handleClose={handleClose}
                  persistCheckingSignupData={persistCheckingSignupData}
                />
              )}
            </StepWrapper>
          }
        />
        <Route
          path="/source_of_funds"
          element={
            <StepWrapper<CustomerSignupValues> name="source_of_funds" previous={previousStepForSourceOfFunds}>
              {stepProps => (
                <SourceOfFundsStep
                  {...stepProps}
                  checkingSignupData={checkingSignupData}
                  onSubmit={persistCheckingSignupData}
                  handleClose={handleClose}
                />
              )}
            </StepWrapper>
          }
        />
        <Route
          path="/submit"
          element={
            <StepWrapper<CustomerSignupValues>
              name="submit"
              next={[
                ['limit_error', when((_, { values }) => values.nextStep === 'limit_error')],
                ['success', when((_, { values }) => values.nextStep === 'success')],
              ]}
              previous={signupState.shouldShowSourceOfFunds ? 'source_of_funds' : 'confirm_addresses'}
            >
              {stepProps => <BeginVerification {...stepProps} handleClose={handleClose} />}
            </StepWrapper>
          }
        />
        <Route
          path="/limit_error"
          element={
            <StepWrapper<CustomerSignupValues> name="limit_error">
              {stepProps => <LimitError {...stepProps} handleClose={handleClose} />}
            </StepWrapper>
          }
        />
        <Route
          path="/success"
          element={
            <StepWrapper<CustomerSignupValues>
              name="success"
              next={[
                ['begin_setup', when(() => signupState.isProspectSignupFlow)],
                ['fund_account', when(() => signupState.isTenuredSignupFlow)],
              ]}
            >
              {stepProps => <Success {...stepProps} />}
            </StepWrapper>
          }
        />
        <Route
          path="/begin_setup"
          element={
            <StepWrapper<CustomerSignupValues> name="begin_setup" next="account_type_selection">
              {stepProps => <BeginSetup {...stepProps} />}
            </StepWrapper>
          }
        />
        <Route
          path="/fund_account"
          element={
            <StepWrapper<CustomerSignupValues> name="fund_account">
              {stepProps => <FundAccount {...stepProps} user={user} handleClose={handleClose} />}
            </StepWrapper>
          }
        />
      </Routes>
    </FlowContextProvider>
  );
};

export default CustomerSignupFlowController;
